import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import {
  PIN_LENGTH,
  erasePinDigit,
  pinStepCount,
  pinStepNumber,
  pressPinDigit,
  startPinFlow,
  type PinFlow,
  type PinIntent,
} from '@player/domain';
import type { FocusPort } from '@player/ports';
import { FONT_STACK, colors } from '../theme.js';
import { useTranslator } from '../i18n/I18nProvider.js';
import { PinPad } from '../components/PinPad.js';

/**
 * Il controllo genitori: impostare, cambiare, disattivare il PIN (SPEC §3.15).
 *
 * ## Il form mancava, e l'interruttore prometteva quel che non c'era
 *
 * Nelle impostazioni c'era una levetta «PIN contenuti per adulti» che accendeva un booleano e
 * nient'altro: nessuna schermata per scegliere le quattro cifre, e quindi nessun PIN. Un controllo
 * genitori acceso senza un PIN è la forma peggiore di funzione dichiarata e mai in vigore — se
 * qualcosa avesse davvero letto quella levetta, avrebbe bloccato del contenuto **senza nessun modo
 * di sbloccarlo**.
 *
 * Adesso la levetta non c'è più: c'è una voce che apre questa schermata, e il PIN è acceso se e solo
 * se quattro cifre sono archiviate.
 *
 * ## Niente scala d'età
 *
 * Il prototipo ha una soglia — 14+ / 16+ / 18+ — e la decisione D-6 l'ha cancellata: funzionerebbe
 * sulla parte di catalogo che ha una classificazione e non sull'altra, e una soglia che copre metà
 * catalogo è **peggio** di nessuna soglia, perché un genitore la imposta e si fida. Vedi la nota in
 * testa a `parentalControl.ts`. Qui il PIN protegge quel che il backend marca `is_adult`.
 *
 * ## Il PIN dimenticato si dice dove serve
 *
 * Non sta sul server — non ci è mai stato — quindi non c'è nessuno a cui chiederlo. La frase compare
 * **sotto il tastierino quando si chiede il PIN attuale**, cioè nell'unico momento in cui qualcuno
 * la sta cercando. Scriverla mentre lo si imposta sarebbe rumore.
 */

export interface PinScreenProps {
  readonly focusPort: FocusPort;
  /** Vero se quattro cifre sono già archiviate su questo apparecchio. */
  readonly pinSet: boolean;
  /** Il PIN archiviato, per verificarlo. `null` quando non ce n'è uno. */
  readonly storedPin: string | null;
  /** Il nuovo PIN, oppure `null` per «disattivato». Chi sta sopra lo archivia. */
  readonly onSave: (pin: string | null) => void;
  readonly onExit: () => void;
}

/**
 * Le due voci del menu, **fuori dal componente**.
 *
 * Erano un array letterale dentro `PinScreen`, quindi ricostruito a ogni resa — e sta fra le
 * dipendenze dell'effetto che si iscrive a `focusPort`. L'effetto si disiscriveva e si riiscriveva
 * **a ogni resa**, comprese quelle in cui qui dentro non era cambiato niente: bastava una resa del
 * genitore.
 *
 * Non è un difetto di spreco: `FocusPort` dà la precedenza **all'ultimo iscritto**, perché è così
 * che un modale cattura il fuoco. Riiscrivendosi, questa schermata tornava in fondo alla fila e si
 * riprendeva la precedenza su un modale aperto sopra di lei.
 *
 * Sono dati fissi — non leggono nessuna prop — quindi qui fuori sono anche al posto giusto.
 *
 * **Non risolve tutto, e va detto.** `flow`, `forgotten` e `cursor` restano fra le dipendenze perché
 * il gestore li legge, quindi premere una freccia riscrive l'iscrizione lo stesso. Quello è un
 * problema di un'altra forma — si tiene il gestore in un `ref` — e riguarda ogni schermata, non
 * questa. Qui si è chiuso il caso in cui non cambiava niente.
 */
const MENU: ReadonlyArray<{
  readonly intent: PinIntent;
  readonly labelKey: string;
  readonly hintKey: string;
}> = [
  { intent: 'change', labelKey: 'pin.menu.change', hintKey: 'pin.menu.changeHint' },
  { intent: 'disable', labelKey: 'pin.menu.disable', hintKey: 'pin.menu.disableHint' },
];

export function PinScreen({ focusPort, pinSet, storedPin, onSave, onExit }: PinScreenProps): ReactNode {
  const translator = useTranslator();

  /*
    `null` = il menu, che si vede solo quando un PIN c'è già.

    Senza PIN non c'è niente da scegliere — cambiarlo e disattivarlo non si applicano — quindi si
    entra direttamente nel percorso d'impostazione: un menu con una voce sola è una pressione
    chiesta per niente.
  */
  const [flow, setFlow] = useState<PinFlow | null>(() => (pinSet ? null : startPinFlow('set')));
  const [cursor, setCursor] = useState(0);
  const [forgotten, setForgotten] = useState(false);

  const handlers = useRef({ onSave, onExit });
  useEffect(() => {
    handlers.current = { onSave, onExit };
  }, [onSave, onExit]);

  const digit = useCallback(
    (value: string) => {
      setFlow((current) => {
        if (current === null) return current;
        const outcome = pressPinDigit(current, value, storedPin);
        if (outcome.kind === 'done') {
          handlers.current.onSave(outcome.pin);
          return null;
        }
        return outcome.flow;
      });
    },
    [storedPin],
  );

  const back = useCallback(() => {
    if (forgotten) {
      setForgotten(false);
      return;
    }
    /*
      Indietro dal tastierino torna al **menu** se un PIN c'è, altrimenti esce.

      Senza PIN il menu non esiste, e fermarsi su una schermata vuota sarebbe un gradino in più che
      non porta da nessuna parte.
    */
    if (flow !== null && pinSet) {
      setFlow(null);
      return;
    }
    handlers.current.onExit();
  }, [flow, pinSet, forgotten]);

  useEffect(() => {
    return focusPort.subscribe((key) => {
      if (key.kind === 'back') {
        back();
        return true;
      }
      if (flow !== null || forgotten) {
        // Col tastierino a schermo le frecce e OK le gestisce `PinPad`, che sa dov'è il suo cursore.
        return false;
      }
      if (key.kind === 'select') {
        const entry = MENU[cursor];
        if (entry) setFlow(startPinFlow(entry.intent));
        return true;
      }
      if (key.kind !== 'direction') return false;
      if (key.direction === 'up') setCursor((at) => Math.max(0, at - 1));
      if (key.direction === 'down') setCursor((at) => Math.min(MENU.length - 1, at + 1));
      return true;
    });
  }, [focusPort, flow, forgotten, cursor, back]);

  return (
    <div
      data-pin-screen="1"
      style={{
        flex: 1,
        minWidth: 0,
        padding: '56px 72px 80px',
        display: 'flex',
        gap: 72,
        boxSizing: 'border-box',
      }}
    >
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 18 }}>
        <span style={{ font: `700 22px ${FONT_STACK}`, color: colors.accent, letterSpacing: '.1em', textTransform: 'uppercase' }}>
          {translator.t('pin.kicker')}
        </span>
        <h1 style={{ font: `800 56px ${FONT_STACK}`, letterSpacing: '-.02em', margin: 0 }}>
          {translator.t(flow === null ? 'pin.title.menu' : `pin.title.${flow.step}`)}
        </h1>

        {flow !== null && (
          <span data-pin-step="1" style={{ font: `600 24px ${FONT_STACK}`, color: colors.textDim }}>
            {translator.t('pin.step', {
              step: pinStepNumber(flow),
              total: pinStepCount(flow.intent),
            })}
          </span>
        )}

        <p style={{ font: `500 24px/1.4 ${FONT_STACK}`, color: colors.textDim, maxWidth: 640, margin: 0 }}>
          {translator.t(pinSet ? 'pin.explain.set' : 'pin.explain.unset')}
        </p>

        {flow === null && !forgotten && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, paddingTop: 12 }}>
            {MENU.map((entry, index) => (
              <button
                key={entry.intent}
                type="button"
                data-f="1"
                data-pin-menu={entry.intent}
                data-focused={cursor === index ? '1' : '0'}
                onClick={() => setFlow(startPinFlow(entry.intent))}
                style={{
                  textAlign: 'left',
                  padding: '18px 26px',
                  border: 0,
                  borderRadius: 14,
                  cursor: 'pointer',
                  background: colors.surface,
                  color: colors.text,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 6,
                  outline: cursor === index ? `4px solid ${colors.accent}` : 'none',
                  outlineOffset: 4,
                }}
              >
                <span style={{ font: `600 28px ${FONT_STACK}` }}>{translator.t(entry.labelKey)}</span>
                <span style={{ font: `500 20px ${FONT_STACK}`, color: colors.textDim }}>
                  {translator.t(entry.hintKey)}
                </span>
              </button>
            ))}
          </div>
        )}

        {/*
          «Ho dimenticato il PIN» compare **solo dove serve**: quando si sta chiedendo il PIN
          attuale, cioè nell'unico momento in cui qualcuno la sta cercando.
        */}
        {flow?.step === 'current' && !forgotten && (
          <button
            type="button"
            data-f="1"
            data-pin-forgot="1"
            onClick={() => setForgotten(true)}
            style={{
              alignSelf: 'flex-start',
              marginTop: 8,
              padding: '10px 18px',
              border: 0,
              borderRadius: 10,
              cursor: 'pointer',
              background: 'transparent',
              color: colors.accent,
              font: `600 22px ${FONT_STACK}`,
            }}
          >
            {translator.t('pin.forgot')}
          </button>
        )}

        {forgotten && (
          <div
            data-pin-forgotten="1"
            style={{
              marginTop: 8,
              padding: 26,
              borderRadius: 14,
              background: colors.surface,
              maxWidth: 640,
              display: 'flex',
              flexDirection: 'column',
              gap: 16,
            }}
          >
            {/*
              **Non si recupera**, e dirlo è tutto quel che si può fare.

              Il PIN non sta sul server — non ci è mai stato — quindi non c'è nessuno a cui
              chiederlo. L'unica strada è cancellare il profilo da questo apparecchio, e questa
              schermata non la esegue: la nomina, e rimanda alle impostazioni. Cancellare
              preferiti, avanzamento e credenziali da dentro una finestra di errore è esattamente
              il genere di scorciatoia che qualcuno preme per sbaglio.
            */}
            <p style={{ margin: 0, font: `500 23px/1.45 ${FONT_STACK}`, color: colors.textMuted }}>
              {translator.t('pin.forgotExplain')}
            </p>
            <button
              type="button"
              data-f="1"
              data-pin-forgot-back="1"
              autoFocus
              onClick={() => setForgotten(false)}
              style={{
                alignSelf: 'flex-start',
                height: 60,
                padding: '0 28px',
                border: 0,
                borderRadius: 12,
                cursor: 'pointer',
                background: colors.surfaceRaised,
                color: colors.text,
                font: `600 23px ${FONT_STACK}`,
              }}
            >
              {translator.t('pin.back')}
            </button>
          </div>
        )}
      </div>

      {flow !== null && !forgotten && (
        <PinPad
          focusPort={focusPort}
          entered={flow.entered.length}
          error={flow.error === null ? null : translator.t(`pin.error.${flow.error}`)}
          onDigit={digit}
          onErase={() => setFlow((current) => (current === null ? current : erasePinDigit(current)))}
          onCancel={back}
          length={PIN_LENGTH}
        />
      )}
    </div>
  );
}
